import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppAuth } from "@/auth/use-app-auth";
import { useNavigate } from "react-router-dom";
import { apex25LegalUrl } from "@/lib/apex25AppUrl";
import {
  CheckCircle,
  Check,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Shield,
  Phone,
  Award,
  Lock,
  X,
  Sparkles,
  Zap,
  Users,
  Clock,
  DollarSign,
  ArrowRight,
  Star,
  CheckCircle2,
} from "lucide-react";
import { useBilling, Plan as BillingPlan } from "../../hooks/useBilling";
import FeaturePacksGrid from "@/components/billing/FeaturePacksGrid";
import { useFeatureMatrix } from "@/hooks/useFeatureMatrix";
import { useUsage } from "../../hooks/useUsage";
import { useAuthFlow, getOrgFromLocation } from "@/lib/auth-flow";
import { Axios } from "@/lib/axios";
import { cn } from "@/lib/utils";
import { MarketingFooter } from "@/components/layout/MarketingFooter";

interface Plan {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  highlight: boolean;
  recommended?: boolean;
}

interface Feature {
  name: string;
  free: boolean | string;
  starter: boolean | string;
  professional: boolean | string;
  business: boolean | string;
  enterprise: boolean | string;
}

interface FAQ {
  question: string;
  answer: string;
}

interface ValidatedCouponState {
  code: string;
  planId: string;
  billingPeriod: "MONTHLY" | "ANNUAL";
  discount: {
    type: "PERCENTAGE" | "FIXED_AMOUNT";
    value: number;
    description: string;
  };
  preview: {
    originalAmount: number;
    discountAmount: number;
    finalAmount: number;
  };
}

// Data: static fallback plans used before API data loads.
const plans: Plan[] = [
  {
    name: "FREE",
    price: "$0",
    period: "/month",
    description: "Perfect for testing",
    features: [
      "1 job post",
      "5 BYOR uploads",
      "2 AI interviews",
      "10 candidate searches",
      "Email support",
    ],
    cta: "Start Free",
    highlight: false,
  },
  {
    name: "STARTER",
    price: "$149",
    period: "/month",
    description: "Solo recruiters & small teams",
    features: [
      "3 job posts",
      "25 BYOR uploads",
      "15 AI interviews",
      "5 intro calls",
      "50 candidate searches",
    ],
    cta: "Subscribe",
    highlight: false,
  },
  {
    name: "PROFESSIONAL",
    price: "$349",
    period: "/month",
    description: "Growing recruitment teams",
    features: [
      "15 job posts",
      "100 BYOR uploads",
      "75 AI interviews",
      "25 intro calls",
      "200 candidate searches",
    ],
    cta: "Subscribe",
    highlight: true,
    recommended: true,
  },
  {
    name: "BUSINESS",
    price: "$899",
    period: "/month",
    description: "Mid-market agencies",
    features: [
      "Unlimited job posts",
      "Unlimited BYOR uploads",
      "250 AI interviews",
      "100 intro calls",
      "500 candidate searches",
    ],
    cta: "Subscribe",
    highlight: false,
  },
  {
    name: "ENTERPRISE",
    price: "Custom",
    period: "",
    description: "Large organizations",
    features: [
      "Everything in Business",
      "Unlimited everything",
      "Custom AI model tuning",
      "Dedicated success manager",
      "SSO / SAML",
    ],
    cta: "Contact Sales",
    highlight: false,
  },
];
const detailedFeatures: Feature[] = [
  {
    name: "Job Posts",
    free: "1",
    starter: "3",
    professional: "15",
    business: "Unlimited",
    enterprise: "Unlimited",
  },
  {
    name: "BYOR Uploads",
    free: "5",
    starter: "25",
    professional: "100",
    business: "Unlimited",
    enterprise: "Unlimited",
  },
  {
    name: "AI Interviews",
    free: "2",
    starter: "15",
    professional: "75",
    business: "250",
    enterprise: "Unlimited",
  },
  {
    name: "AI Rankings",
    free: false,
    starter: "10",
    professional: "50",
    business: "Unlimited",
    enterprise: "Unlimited",
  },
  {
    name: "Candidate Searches",
    free: "10",
    starter: "50",
    professional: "200",
    business: "500",
    enterprise: "Unlimited",
  },
  {
    name: "Intro Calls",
    free: false,
    starter: "5",
    professional: "25",
    business: "100",
    enterprise: "Unlimited",
  },
  {
    name: "AI Candidate Matching",
    free: true,
    starter: true,
    professional: true,
    business: true,
    enterprise: true,
  },
  {
    name: "Automated Screening",
    free: true,
    starter: true,
    professional: true,
    business: true,
    enterprise: true,
  },
  {
    name: "Interview Scheduling",
    free: false,
    starter: true,
    professional: true,
    business: true,
    enterprise: true,
  },
  {
    name: "Custom Branding",
    free: false,
    starter: false,
    professional: true,
    business: true,
    enterprise: true,
  },
  {
    name: "Advanced Analytics",
    free: false,
    starter: false,
    professional: true,
    business: true,
    enterprise: true,
  },
  {
    name: "Team Collaboration",
    free: false,
    starter: true,
    professional: true,
    business: true,
    enterprise: true,
  },
  {
    name: "API Access",
    free: false,
    starter: false,
    professional: false,
    business: true,
    enterprise: true,
  },
  {
    name: "Single Sign-On (SSO)",
    free: false,
    starter: false,
    professional: false,
    business: false,
    enterprise: true,
  },
  {
    name: "Dedicated Support",
    free: false,
    starter: false,
    professional: false,
    business: true,
    enterprise: true,
  },
  {
    name: "Email Support",
    free: true,
    starter: true,
    professional: true,
    business: true,
    enterprise: true,
  },
  {
    name: "Priority Support",
    free: false,
    starter: true,
    professional: true,
    business: true,
    enterprise: true,
  },
  {
    name: "Phone Support",
    free: false,
    starter: false,
    professional: true,
    business: true,
    enterprise: true,
  },
];

const faqs: FAQ[] = [
  {
    question: "Can I change my plan later?",
    answer:
      "Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any billing adjustments.",
  },
  {
    question: "What happens if I exceed my plan limits?",
    answer:
      "We'll notify you when you're approaching your limits. You can either upgrade your plan or purchase additional credits for the current month.",
  },
  {
    question: "Do you offer annual billing discounts?",
    answer:
      "Yes! Save 17% when you pay annually. Annual plans also include additional features and priority support.",
  },
  {
    question: "Is there a setup fee?",
    answer:
      "No setup fees, ever. You only pay for your chosen plan. Enterprise customers get free onboarding and training.",
  },
  {
    question: "How secure is my data?",
    answer:
      "Your data is encrypted in transit and at rest. We never share your candidate data with third parties.",
  },
  {
    question: "Can I cancel anytime?",
    answer:
      "Yes, you can cancel your subscription at any time. No long-term contracts or cancellation fees. Your access continues until the end of your billing period.",
  },
  {
    question: "Do you offer refunds?",
    answer:
      "We offer a 30-day money-back guarantee. If you're not satisfied within the first 30 days, we'll provide a full refund.",
  },
];

// Components
const Hero = () => (
  <section className="relative overflow-hidden pt-28" style={{ background: "#faf9f4" }}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
      <div className="text-center pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
          className="space-y-5"
        >
          <div className="inline-flex items-center gap-2 border border-[#e4c787] bg-[#f8f3e8] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[#7b5804]">
            <Sparkles className="w-3.5 h-3.5" />
            <span>AI-Powered Recruitment Platform</span>
          </div>

          <h1 className="mx-auto max-w-5xl font-display text-5xl font-bold leading-[0.95] tracking-tight text-[#1b1c19] md:text-7xl">
            Choose the right plan for your hiring needs.
          </h1>

          <p className="mx-auto max-w-2xl text-lg leading-relaxed text-[#4e4637]">
            Scale your recruitment with flexible pricing. Upgrade anytime, cancel anytime.
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);

// Quiz removed
// RecommendationBanner removed
// SocialProof section removed for tighter layout

const PricingTable = () => {
  const [billingInterval, setBillingInterval] = useState<"MONTHLY" | "ANNUAL">("MONTHLY");
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoPlanId, setPromoPlanId] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [validatedCoupon, setValidatedCoupon] = useState<ValidatedCouponState | null>(null);
  const { plans, subscription, upgradeSubscription, loading, error } = useBilling();
  const { usage } = useUsage();
  const { isAuthenticated } = useAppAuth();
  const startAuth = useAuthFlow();
  const navigate = useNavigate();
  const { matrix, loading: matrixLoading } = useFeatureMatrix();
  const orgAuthId = getOrgFromLocation();

  const getPlanNameKey = (plan: BillingPlan) => (plan.displayName || plan.name || "").toUpperCase();
  const promoEligiblePlans = Array.isArray(plans)
    ? plans.filter((plan) => {
        const nameKey = getPlanNameKey(plan);
        return nameKey !== "FREE" && nameKey !== "ENTERPRISE";
      })
    : [];

  useEffect(() => {
    if (!promoEligiblePlans.length) return;
    if (!promoPlanId || !promoEligiblePlans.some((plan) => plan.id === promoPlanId)) {
      setPromoPlanId(promoEligiblePlans[0].id);
    }
  }, [promoEligiblePlans, promoPlanId]);

  useEffect(() => {
    if (validatedCoupon) {
      setValidatedCoupon(null);
    }
    setPromoError(null);
  }, [billingInterval]);

  const formatCurrency = (value: number) =>
    `$${Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const validatePromoCode = async (
    targetPlanId: string,
    rawCode?: string
  ): Promise<ValidatedCouponState | null> => {
    const code = (rawCode || promoCode).trim();
    if (!code) {
      setPromoError("Enter a promo code to continue.");
      setValidatedCoupon(null);
      return null;
    }
    if (!targetPlanId) {
      setPromoError("Select a plan to validate your promo code.");
      setValidatedCoupon(null);
      return null;
    }

    setPromoLoading(true);
    setPromoError(null);
    try {
      const response = await Axios.post("/api/billing/coupon/validate", {
        code,
        planId: targetPlanId,
        billingPeriod: billingInterval,
      });

      if (!response?.data?.valid) {
        const errorMessage = response?.data?.error || "Promo code is not valid for this plan.";
        setPromoError(errorMessage);
        setValidatedCoupon(null);
        return null;
      }

      const nextCoupon: ValidatedCouponState = {
        code,
        planId: targetPlanId,
        billingPeriod: billingInterval,
        discount: response.data.discount,
        preview: response.data.preview,
      };
      setValidatedCoupon(nextCoupon);
      setPromoPlanId(targetPlanId);
      return nextCoupon;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Unable to validate promo code right now.";
      setPromoError(errorMessage);
      setValidatedCoupon(null);
      return null;
    } finally {
      setPromoLoading(false);
    }
  };

  const handlePlanClick = async (plan: BillingPlan) => {
    const nameKey = getPlanNameKey(plan);
    if (!isAuthenticated) {
      await startAuth({ mode: "login", returnTo: "/pricing", orgAuthId });
      return;
    }

    if (nameKey === "FREE") {
      // Free plan - redirect to dashboard
      navigate("/dashboard");
      return;
    }

    if (nameKey === "ENTERPRISE") {
      // Enterprise - contact sales
      window.open("mailto:apex25@theresume.io?subject=Enterprise Plan Inquiry", "_blank");
      return;
    }

    // Check if user already has this plan
    if (subscription?.plan?.id === plan.id) {
      navigate("/dashboard");
      return;
    }

    try {
      const rawPromoCode = promoCode.trim();
      let couponToApply: string | undefined;

      if (rawPromoCode) {
        const hasMatchingValidatedCoupon =
          !!validatedCoupon &&
          validatedCoupon.code.toUpperCase() === rawPromoCode.toUpperCase() &&
          validatedCoupon.planId === plan.id &&
          validatedCoupon.billingPeriod === billingInterval;

        if (hasMatchingValidatedCoupon) {
          couponToApply = rawPromoCode;
        } else {
          const couponResult = await validatePromoCode(plan.id, rawPromoCode);
          if (!couponResult) {
            alert("Promo code could not be applied to this plan.");
            return;
          }
          couponToApply = couponResult.code;
        }
      }

      await upgradeSubscription(plan.id, billingInterval, couponToApply);
    } catch (error: any) {
      console.error("Failed to upgrade:", error);
      if (error?.response?.status === 403) {
        alert(
          "Only your organization admin can upgrade the plan. Please ask your admin to log in and purchase a subscription for the organization."
        );
      } else {
        alert("Failed to start upgrade process. Please try again.");
      }
    }
  };

  const getButtonText = (plan: BillingPlan) => {
    const nameKey = (plan.displayName || plan.name || "").toUpperCase();
    if (!isAuthenticated) {
      return nameKey === "FREE" ? "Start Free" : "Subscribe";
    }

    if (subscription?.plan?.id === plan.id) {
      return "Current Plan";
    }

    if (nameKey === "FREE") {
      return "Downgrade";
    }

    if (nameKey === "ENTERPRISE") {
      return "Contact Sales";
    }

    return subscription ? "Upgrade" : "Subscribe";
  };

  const isCurrentPlan = (plan: BillingPlan) => {
    return subscription?.plan?.id === plan.id;
  };

  if (loading) {
    return (
      <section className="bg-[#f6f1e6] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mx-auto h-8 w-8 animate-spin border-b border-[#7b5804]"></div>
            <p className="mt-2 text-[#4e4637]">Loading plans...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || !Array.isArray(plans)) {
    return (
      <section className="bg-[#f6f1e6] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="mb-4 text-[#9a3412]">Failed to load pricing plans</p>
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg bg-[#1b1c19] px-4 py-2 text-white hover:bg-[#11120f]"
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden bg-[#faf9f4] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          viewport={{ once: true }}
          className="text-center mb-6"
        >
          <div className="inline-flex items-center gap-2 border border-[#e7dcc4] bg-white p-1.5">
            <button
              onClick={() => setBillingInterval("MONTHLY")}
              className={`px-6 py-2.5 text-sm font-semibold transition-colors duration-150 ${
                billingInterval === "MONTHLY"
                  ? "bg-[#1b1c19] text-[#faf9f4]"
                  : "text-[#4e4637] hover:bg-[#f8f3e8] hover:text-[#1b1c19]"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval("ANNUAL")}
              className={`px-6 py-2.5 text-sm font-semibold transition-colors duration-150 relative ${
                billingInterval === "ANNUAL"
                  ? "bg-[#1b1c19] text-[#faf9f4]"
                  : "text-[#4e4637] hover:bg-[#f8f3e8] hover:text-[#1b1c19]"
              }`}
            >
              Annual
              <span className="absolute -right-2 -top-2 border border-[#7b5804] bg-[#d4a853] px-2 py-0.5 text-xs font-bold text-[#1b1c19]">
                Save 17%
              </span>
            </button>
          </div>

          <div className="max-w-2xl mx-auto mt-5">
            <button
              onClick={() => setShowPromoInput((prev) => !prev)}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#4e4637] hover:text-[#1b1c19]"
            >
              Have a promo code?
              {showPromoInput ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>

            <AnimatePresence>
              {showPromoInput && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="mt-3 border border-[#e7dcc4] bg-white p-4 text-left"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(event) => {
                        setPromoCode(event.target.value);
                        setValidatedCoupon(null);
                        setPromoError(null);
                      }}
                      placeholder="Enter promo code"
                      className="md:col-span-2 w-full border border-[#d7ccb8] bg-[#fffdf8] px-3 py-2 text-sm text-[#1b1c19] focus:outline-none focus:ring-2 focus:ring-[#d4a853]"
                    />
                    <select
                      value={promoPlanId}
                      onChange={(event) => {
                        setPromoPlanId(event.target.value);
                        setValidatedCoupon(null);
                        setPromoError(null);
                      }}
                      className="w-full border border-[#d7ccb8] bg-[#fffdf8] px-3 py-2 text-sm text-[#1b1c19] focus:outline-none focus:ring-2 focus:ring-[#d4a853]"
                    >
                      {promoEligiblePlans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.displayName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <button
                      onClick={() => void validatePromoCode(promoPlanId)}
                      disabled={promoLoading || !promoCode.trim() || !promoPlanId}
                      className="inline-flex items-center gap-2 border border-[#7b5804] bg-[#7b5804] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5f4303] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {promoLoading ? "Validating..." : "Apply Promo Code"}
                    </button>

                    {validatedCoupon && (
                      <span className="inline-flex items-center gap-1 border border-[#e4c787] bg-[#f8f3e8] px-3 py-1 text-xs font-semibold text-[#7b5804]">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Code applied
                      </span>
                    )}
                  </div>

                  {promoError && <p className="mt-3 text-sm text-[#9a3412]">{promoError}</p>}

                  {validatedCoupon && (
                    <div className="mt-3 border border-[#e4c787] bg-[#f8f3e8] p-3 text-sm text-[#5f4303]">
                      <div className="font-semibold">
                        {validatedCoupon.discount.type === "PERCENTAGE"
                          ? `${validatedCoupon.discount.value}% off`
                          : `${formatCurrency(validatedCoupon.discount.value)} off`}
                      </div>
                      <div className="mt-1 text-[#7b5804]">
                        {formatCurrency(validatedCoupon.preview.originalAmount)} -{" "}
                        {formatCurrency(validatedCoupon.preview.discountAmount)} ={" "}
                        {formatCurrency(validatedCoupon.preview.finalAmount)} (
                        {validatedCoupon.billingPeriod.toLowerCase()})
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 lg:gap-6">
          {Array.isArray(plans) &&
            plans
              .filter(Boolean)
              .sort((a, b) => {
                // Define the desired order: Free -> Starter -> Professional -> Business -> Enterprise
                const order = { FREE: 1, STARTER: 2, PROFESSIONAL: 3, BUSINESS: 4, ENTERPRISE: 5 };
                const aKey = (a.displayName || a.name || "").toUpperCase();
                const bKey = (b.displayName || b.name || "").toUpperCase();
                return (
                  (order[aKey as keyof typeof order] || 99) -
                  (order[bKey as keyof typeof order] || 99)
                );
              })
              .map((plan, index) => {
                // Guard against unexpected nulls
                if (!plan) return null as any;
                const nameKey = (plan.displayName || plan.name || "").toUpperCase();
                const isPopular = nameKey === "PROFESSIONAL";
                const isCurrent = isCurrentPlan(plan);
                const priceText =
                  billingInterval === "ANNUAL"
                    ? (plan as any).priceAnnual || plan.price
                    : plan.price;
                // Build dynamic bullet list using the feature matrix
                const tierKey =
                  nameKey === "FREE"
                    ? "FREE"
                    : nameKey === "STARTER"
                    ? "STARTER"
                    : nameKey === "PROFESSIONAL"
                    ? "PROFESSIONAL"
                    : nameKey === "BUSINESS"
                    ? "BUSINESS"
                    : "ENTERPRISE";
                const getVal = (key: string) => {
                  const f = matrix?.features.find((x: any) => x.key === key);
                  if (!f) return { v: undefined as unknown as number | null, p: true };
                  return { v: f.tiers[tierKey] as number | null, p: f.isPackable as boolean };
                };
                const fmt = (key: string, label: string) => {
                  const { v, p } = getVal(key);
                  if (v === undefined) return null;
                  if (v === null) return `${p ? "Unlimited" : "Included"} ${label}`;
                  if (v === 0) return `${label} upgrade required`;
                  return `${v} ${label}`;
                };
                const features =
                  matrix && !matrixLoading
                    ? ([
                        fmt("active_jobs", "job posts"),
                        fmt("byor_uploads", "resumes processed"),
                        fmt("ai_interviews", "AI interviews"),
                        fmt("intro_calls", "intro calls"),
                        fmt("candidate_searches", "candidate searches"),
                        fmt("applied_candidates_search", "applied searches"),
                        fmt("ai_candidate_ranking", "AI ranking"),
                        fmt("candidate_profile_view", "profile view"),
                      ].filter(Boolean) as string[])
                    : [
                        `${plan.jobPostLimit || "Unlimited"} job posts`,
                        `${plan.resumeLimit} resumes processed`,
                        `${plan.interviewLimit} AI interviews`,
                        plan.introCallLimit > 0
                          ? `${plan.introCallLimit} intro calls`
                          : "intro calls upgrade required",
                      ];

                return (
                  <motion.div
                    key={plan.id || `${nameKey}-${index}`}
                    className={`group relative flex flex-col overflow-hidden border transition-colors duration-150 ${
                      isPopular
                        ? "z-20 border-[#7b5804]"
                        : "z-10 border-[#f5ecda] hover:border-[#d4a853]"
                    }`}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div
                      className={cn(
                        "absolute inset-0",
                        isPopular ? "bg-[#1b1c19]" : isCurrent ? "bg-[#f8f3e8]" : "bg-white"
                      )}
                    />

                    {/* Content */}
                    <div className="relative p-6 flex flex-col h-full">
                      {/* Badge */}
                      {isPopular && (
                        <div className="absolute right-4 top-4">
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="flex items-center gap-1.5 border border-[#d4a853] bg-[#d4a853] px-3 py-1.5 text-xs font-bold text-[#1b1c19]"
                          >
                            <Star className="w-3 h-3 fill-slate-900" />
                            MOST POPULAR
                          </motion.div>
                        </div>
                      )}

                      {isCurrent && (
                        <div className="absolute right-4 top-4">
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="flex items-center gap-1.5 border border-[#d4a853] bg-[#f0d08a] px-3 py-1.5 text-xs font-bold text-[#1b1c19]"
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            CURRENT PLAN
                          </motion.div>
                        </div>
                      )}

                      {/* Plan name */}
                      <h3
                        className={`mb-2 mt-4 text-sm font-bold uppercase tracking-wider ${
                          isPopular ? "text-[#d4a853]" : "text-[#7d7465]"
                        }`}
                      >
                        {plan.displayName}
                      </h3>

                      {/* Price */}
                      <div className="mb-1.5">
                        {nameKey === "ENTERPRISE" ? (
                          <div className="flex items-baseline gap-1">
                            <span
                              className="text-3xl font-bold text-slate-900"
                              style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
                            >
                              Contact Us
                            </span>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-baseline gap-1">
                              <span
                                className={`text-5xl font-bold ${
                                  isPopular ? "text-white" : "text-slate-900"
                                }`}
                                style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
                              >
                                {priceText}
                              </span>
                              <span
                                className={`text-sm ${
                                  isPopular ? "text-[rgba(250,249,244,0.55)]" : "text-[#7d7465]"
                                }`}
                              >
                                /{billingInterval === "ANNUAL" ? "year" : "month"}
                              </span>
                            </div>
                            {billingInterval === "ANNUAL" && priceText !== "$0" && (
                              <p
                                className={`mt-1 text-xs font-semibold ${
                                  isPopular ? "text-[#f0d08a]" : "text-[#7b5804]"
                                }`}
                              >
                                Save $
                                {Math.round(
                                  parseFloat(priceText.replace("$", "").replace(",", "")) *
                                    12 *
                                    0.17
                                ).toLocaleString()}{" "}
                                per year
                              </p>
                            )}
                          </>
                        )}
                      </div>

                      {/* Description */}
                      <p
                        className={`mb-4 text-sm ${
                          isPopular ? "text-[rgba(250,249,244,0.62)]" : "text-[#4e4637]"
                        }`}
                      >
                        {plan.description}
                      </p>

                      {/* CTA Button */}
                      <button
                        onClick={() => handlePlanClick(plan)}
                        disabled={isCurrent}
                        className={`w-full py-3 px-6 border font-semibold transition-colors duration-150 focus:outline-none focus:ring-4 focus:ring-slate-500/20 text-sm mb-4 ${
                          isCurrent
                            ? "cursor-not-allowed bg-[#efe7d7] text-[#7d7465]"
                            : isPopular
                            ? "border-[#d4a853] bg-[#d4a853] text-[#1b1c19] hover:bg-[#e4bc6a]"
                            : nameKey === "ENTERPRISE"
                            ? "border-[#1b1c19] bg-[#1b1c19] text-white hover:bg-[#11120f]"
                            : "border-[#1b1c19] bg-[#1b1c19] text-white hover:bg-[#7b5804]"
                        }`}
                      >
                        <span className="flex items-center justify-center gap-2">
                          {getButtonText(plan)}
                          {!isCurrent && (
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          )}
                        </span>
                      </button>

                      {/* Features */}
                      <div className="space-y-2.5 flex-grow">
                        {features.slice(0, 6).map((feature, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 * i }}
                            viewport={{ once: true }}
                            className="flex items-center gap-3"
                          >
                            {/upgrade required|not available/i.test(feature) ? (
                              <div
                                className={`flex-shrink-0 w-5 h-5 border flex items-center justify-center ${
                                  isPopular
                                    ? "border-[#3a3127] bg-[#2a241d]"
                                    : "border-[#e7dcc4] bg-[#f3eee4]"
                                }`}
                              >
                                <X
                                  className={`w-3 h-3 ${
                                    isPopular ? "text-[rgba(250,249,244,0.32)]" : "text-[#a39581]"
                                  }`}
                                />
                              </div>
                            ) : (
                              <div
                                className={`flex-shrink-0 w-5 h-5 border flex items-center justify-center ${
                                  isPopular
                                    ? "border-[#d4a853]/30 bg-[#d4a853]/20"
                                    : "border-[#e7dcc4] bg-[#f8f3e8]"
                                }`}
                              >
                                <Check
                                  className={`w-3 h-3 ${
                                    isPopular ? "text-[#f0d08a]" : "text-[#7b5804]"
                                  }`}
                                />
                              </div>
                            )}
                            <span
                              className={`text-sm font-medium ${
                                isPopular ? "text-[rgba(250,249,244,0.80)]" : "text-[#3f392e]"
                              }`}
                            >
                              {feature !== "-" ? feature : "Included"}
                            </span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
        </div>

        <p className="mt-8 text-center text-sm text-[#4e4637]">
          By subscribing you agree to the Apex 25{" "}
          <a
            href={apex25LegalUrl("/terms")}
            className="font-semibold underline underline-offset-2 hover:text-[#1b1c19]"
          >
            Customer Terms of Service
          </a>
          , including how subscriptions renew. Our{" "}
          <a
            href={apex25LegalUrl("/privacy")}
            className="font-semibold underline underline-offset-2 hover:text-[#1b1c19]"
          >
            Privacy Policy
          </a>{" "}
          explains how we handle your information.
        </p>

        {/* Additional info removed (trial messaging) */}
      </div>
    </section>
  );
};

const FeatureComparison = () => {
  const [showAll, setShowAll] = useState(false);
  const { matrix, loading, error } = useFeatureMatrix();

  const renderValue = (limit: number | null, isPackable: boolean) => {
    if (limit === null) return isPackable ? "Unlimited" : "Included";
    if (limit === 0) return <X className="w-4 h-4 text-slate-400 mx-auto" />;
    return <span className="text-slate-900 font-medium text-sm">{limit}</span>;
  };

  const curatedOrder = [
    "active_jobs",
    "byor_uploads",
    "ai_interviews",
    "intro_calls",
    "candidate_searches",
    "applied_candidates_search",
    "ai_candidate_ranking",
    "candidate_profile_view",
    "resume_preview_view",
    "spotlight_intro_view",
    "interview_video_view",
    "interview_analysis_view",
  ];

  const features = matrix?.features
    ? matrix.features
        .filter((f) => curatedOrder.includes(f.key))
        .sort((a, b) => curatedOrder.indexOf(a.key) - curatedOrder.indexOf(b.key))
    : [];

  const visible = showAll ? features : features.slice(0, 10);

  return (
    <section className="relative overflow-hidden bg-[#faf9f4] py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="mb-4 inline-flex items-center gap-2 border border-[#e4c787] bg-[#f8f3e8] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[#7b5804]">
            <CheckCircle2 className="w-4 h-4" />
            Full Feature Breakdown
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Compare <span className="text-[#7b5804]">Everything</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg leading-relaxed text-[#4e4637]">
            See exactly what's included in each plan with our detailed comparison
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="relative overflow-hidden border border-[#e7dcc4] bg-white"
        >
          <div className="overflow-x-auto relative z-10">
            <table className="w-full">
              <thead>
                <tr className="bg-[#f8f3e8]/70">
                  <th className="sticky left-0 z-20 bg-[#f8f3e8] p-6 text-left text-base font-bold text-[#1b1c19]">
                    Features
                  </th>
                  {matrix?.tierOrder.map((t) => (
                    <th
                      key={t}
                      className={`text-center p-6 font-bold text-base ${
                        t === "PROFESSIONAL" ? "bg-[#1b1c19] text-white" : "text-[#1b1c19]"
                      }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span>{matrix.tierDisplayNames[t]}</span>
                        {t === "PROFESSIONAL" && (
                          <span className="text-xs font-normal text-[#f0d08a]">Recommended</span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-5 w-5 animate-spin border-b border-[#7b5804]"></div>
                        <span className="text-[#4e4637]">Loading features...</span>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-[#9a3412]">
                      Failed to load feature matrix
                    </td>
                  </tr>
                ) : (
                  visible.map((f, idx) => (
                    <motion.tr
                      key={f.key}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      viewport={{ once: true }}
                      className={cn(
                        "border-t border-[#efe6d5] transition-colors hover:bg-[#fbf8f2]",
                        idx % 2 === 0 ? "bg-transparent" : "bg-[#faf6ef]/60"
                      )}
                    >
                      <td className="sticky left-0 z-10 border-r border-[#efe6d5] bg-white p-5 font-medium text-[#1b1c19]">
                        {f.name}
                      </td>
                      {matrix!.tierOrder.map((t) => (
                        <td
                          key={t}
                          className={cn(
                            "p-5 text-center",
                            t === "PROFESSIONAL" ? "bg-[#f8f3e8]" : ""
                          )}
                        >
                          <div className="flex items-center justify-center">
                            {renderValue(f.tiers[t], f.isPackable)}
                          </div>
                        </td>
                      ))}
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          viewport={{ once: true }}
          className="text-center mt-8"
        >
          <button
            onClick={() => setShowAll(!showAll)}
            className="group inline-flex items-center gap-2 border border-[#2d261d] bg-[#1b1c19] px-6 py-3 font-semibold text-white transition-colors duration-150 hover:bg-[#7b5804]"
          >
            {showAll ? (
              <>
                Show Less Features
                <ChevronUp className="w-4 h-4 group-hover:-translate-y-1 transition-transform" />
              </>
            ) : (
              <>
                Show All Features
                <ChevronDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
              </>
            )}
          </button>
        </motion.div>
      </div>
    </section>
  );
};

const FAQ = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  return (
    <section className="relative overflow-hidden bg-[#faf9f4] py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="mb-4 inline-flex items-center gap-2 border border-[#e4c787] bg-[#f8f3e8] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[#7b5804]">
            <Sparkles className="h-4 w-4" />
            Got Questions?
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Frequently Asked <span className="text-[#7b5804]">Questions</span>
          </h2>
          <p className="text-lg leading-relaxed text-[#4e4637]">
            Everything you need to know about our pricing and plans
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openFAQ === index;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="group"
              >
                <div
                  className={cn(
                    "bg-white border overflow-hidden transition-colors duration-150",
                    isOpen
                      ? "border-[#d4a853] ring-1 ring-inset ring-[#d4a853]/10"
                      : "border-[#e7dcc4] hover:border-[#d4a853]"
                  )}
                >
                  <button
                    onClick={() => setOpenFAQ(isOpen ? null : index)}
                    className="w-full text-left p-6 focus:outline-none"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="pr-8 text-lg font-semibold text-[#1b1c19]">{faq.question}</h3>
                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className={cn(
                          "flex-shrink-0 w-8 h-8 border flex items-center justify-center transition-colors",
                          isOpen ? "bg-[#f2e5c7]" : "bg-[#f3eee4] group-hover:bg-[#f8f3e8]"
                        )}
                      >
                        <ChevronDown
                          className={cn(
                            "w-5 h-5 transition-colors",
                            isOpen ? "text-[#7b5804]" : "text-[#4e4637]"
                          )}
                        />
                      </motion.div>
                    </div>
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 pt-0">
                          <div className="border-t border-[#efe6d5] pt-4">
                            <p className="leading-relaxed text-[#4e4637]">{faq.answer}</p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="relative inline-flex flex-col items-center gap-3 overflow-hidden border border-[#e4c787] bg-[#f8f3e8] p-8">
            <p className="relative z-10 font-medium text-[#4e4637]">Still have questions?</p>
            <button
              onClick={() => window.open("mailto:apex25@theresume.io", "_blank")}
              className="inline-flex items-center gap-2 border border-[#1b1c19] bg-[#1b1c19] px-6 py-3 font-semibold text-white transition-colors duration-150 hover:bg-[#7b5804]"
            >
              <span>Contact Support</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const { isAuthenticated } = useAppAuth();
  const navigate = useNavigate();
  const startAuth = useAuthFlow();
  const orgAuthId = getOrgFromLocation();

  const handleGetStarted = async () => {
    if (!isAuthenticated) {
      await startAuth({ mode: "login", returnTo: "/pricing", orgAuthId });
    } else {
      navigate("/pricing");
    }
  };

  return (
    <section className="technical-dark-section relative overflow-hidden border-t border-[#2c2418] bg-[#1b1c19] py-28">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
          className="space-y-7"
        >
          <div className="inline-flex items-center gap-2 border border-[rgba(212,168,83,0.35)] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[#f0c76c]">
            <Sparkles className="w-3.5 h-3.5" />
            Pricing built for operating teams
          </div>

          <h2 className="font-display text-4xl font-bold leading-tight text-white md:text-6xl">
            Ready to transform your hiring process?
          </h2>

          <p className="mx-auto mb-10 max-w-3xl text-lg leading-relaxed text-[rgba(250,249,244,0.72)]">
            Modern recruiting powered by AI: streamline sourcing, screening, and interviews.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleGetStarted}
              className="group flex items-center gap-2 border border-[#d4a853] bg-[#d4a853] px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-[#1b1c19] transition-colors duration-150 hover:bg-[#e4bc6a]"
            >
              Get Started
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() =>
                window.open("mailto:apex25@theresume.io?subject=Enterprise Plan Inquiry", "_blank")
              }
              className="border border-[rgba(212,168,83,0.35)] bg-transparent px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors duration-150 hover:border-[#d4a853] hover:bg-[#2a241d]"
            >
              Contact Sales
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Main Pricing Component
export const Pricing = () => {
  return (
    <div
      className="technical-home font-body antialiased"
      style={{ background: "#faf9f4", color: "#1b1c19" }}
    >
      <Hero />
      <PricingTable />
      <FeaturePacksGrid />
      <FeatureComparison />
      <FAQ />
      <CTASection />
      <MarketingFooter />
    </div>
  );
};
